import { asWidgetProps } from 'Src/types/widget-helpers';
import React, { ComponentProps, useState } from 'react';
import { t } from 'Src/i18n';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import type { TrackingPayload } from 'Tracking/tracking-api/useTracking';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { DISPLAY_VIEWPORT, DISPLAY_REM } from 'Components/frameworks/image/constants';
import { Scrollable } from 'Components/frameworks/scrollable/Scrollable';
import { Slider } from 'Components/frameworks/slider/Slider';
import { NavTag } from 'Components/frameworks/nav-tag/NavTag';
import { Product } from 'Components/widgets/product/Product';
import type { ProductProps } from 'Components/widgets/product/Product.utils';
import 'Components/widgets/product/Product.utils';
type Products = ReadonlyArray<
  ProductProps & {
    offer?: Offer;
    onComparisonClick?: () => unknown;
    onClick?: (arg0: React.SyntheticEvent<HTMLElement>) => void;
  }
>;

type Props = {
  lineName?: string;
  tags: ReadonlyArray<{
    label: string;
    products: Products;
  }>;
  trackingData: TrackingPayload;
};
const RATIO_DISPLAYS = [
  {
    value: 3 / 10,
    unit: DISPLAY_REM,
  },
  {
    value: 360,
    unit: 'px',
  },
  {
    value: 360,
    unit: 'px',
  },
  {
    value: 85 / 100,
    unit: DISPLAY_VIEWPORT,
  },
];

export const ProductTags = ({ lineName, tags, trackingData }: Props) => {
  // use isDesktop to display slider's controls only on desktops
  const { isTablet, isDesktop } = useResponsive();
  const [selectedTag, setSelectedTag] = useState(0);
  const track = useTracking(trackingData);
  const { products, label: selectedTagLabel } = tags[selectedTag];
  return (
    <div className="flex w-full max-w-site flex-wrap">
      <Scrollable
        {...asWidgetProps<ComponentProps<typeof Scrollable>>({
          backwardLabel: t('common.scrollBackward'),
          childrenWrapperClassNames: 'flex gap-10 mb-10 px-20 lg:px-0',
          className: '-mx-20 mb-15 w-screen lg:mx-0 lg:w-full',
          forwardLabel: t('common.scrollForward'),
        })}
      >
        {tags.map((tag, index) => (
          <NavTag
            key={tag.label}
            {...asWidgetProps<ComponentProps<typeof NavTag>>({
              isActive: index === selectedTag,
              onClick: () => setSelectedTag(index),
              trackingData: { ...trackingData, labelGA: `${tag.label}_tab` },
            })}
          >
            {tag.label}
          </NavTag>
        ))}
      </Scrollable>

      <Slider
        {...asWidgetProps<ComponentProps<typeof Slider>>({
          className: '-mx-20 w-screen lg:mx-0 lg:w-full',
          cols: 0,
          hasControls: isDesktop && products.length > 3,
          navPosition: 'center',
          nbItemsToScroll: isTablet ? 2 : 1,
          nextLabel: t('common.next'),
          previousLabel: t('common.previous'),
          scrollAreaContainerClassName:
            'gap-20 px-20 lg:px-0 scrollbar-hidden scroll-x scroll-smooth',
          scrollControlsClassName: '-mx-20 lg:mx-0',
        })}
      >
        {products.map((product, index) => (
          <div key={product.id} className="w-[85vw] max-w-360">
            <Product
              {...asWidgetProps<ComponentProps<typeof Product>>({
                ...product,
                ratioDisplay: RATIO_DISPLAYS,
                index: index,
                lineName: lineName,
                onClick: () =>
                  track({
                    labelGA: `${selectedTagLabel}_${product.id}`,
                  }),
              })}
            />
          </div>
        ))}
      </Slider>
    </div>
  );
};
